import { createTokenHeader } from '../utils/functions/headers';
import {
  TExportRetailersRequestBody,
  TReferrerHistoryImportResponse,
  TRetailer,
  TVerifier,
  TgetRetailersParams,
  KYCHistoryList,
} from '../utils/types/retailers';
import { emptySplitApi } from './emptySplitApi';
import {
  GeneralObject,
  TDisableCreditPeriodPayload,
  TEnableCreditPeriodPayload,
} from '../utils/types';

export const retailerService = emptySplitApi
  .enhanceEndpoints({ addTagTypes: ['list'] })
  .injectEndpoints({
    endpoints: (builder) => ({
      getRetailerInfo: builder.query<TRetailer, number>({
        query: (retailerId) => ({
          method: 'GET',
          url: `retailers/${retailerId}`,
        }),
      }),
      getAllVerifierNames: builder.query<TVerifier[], void>(
        {
          query: () => ({
            method: 'GET',
            url: 'kycVerifier/getAllVerifiersList',
          }),
        }
      ),
      getAllRetailers: builder.query<
        { data: TRetailer[]; totalCount: number },
        TgetRetailersParams
      >({
        query: ({
          pageNo,
          pageSize,
          searchString,
          filters,
        }) => ({
          method: 'GET',
          url: 'retailers/list',
          params: {
            page: pageNo,
            size: pageSize,
            searchKey: searchString ? searchString : null,
            plants: filters.plantCode,
            status: filters.kycStatus,
            faName: filters.faName,
            docStatus: filters.documentStatus,
            verifierId: filters.verifier,
          },
        }),
        transformResponse: (
          data: TRetailer[],
          meta: Response
        ) => {
          const totalCount =
            meta.headers.get('X-total-count');
          return {
            data,
            totalCount: Number(totalCount ? totalCount : 0),
          };
        },
        providesTags: ['list'],
      }),
      downloadLSMWFile: builder.query<
        { url?: string },
        number[]
      >({
        query: (idArray) => ({
          method: 'POST',
          url: 'retailers/download/lsmw',
          data: idArray,
        }),
      }),
      referrerHistoryImport: builder.mutation<
        TReferrerHistoryImportResponse,
        FormData
      >({
        query: (data) => {
          return {
            url: 'app-user/referral/import',
            method: 'POST',
            data,
            headers: createTokenHeader(),
          };
        },
      }),
      retailersImport: builder.mutation<
        TReferrerHistoryImportResponse,
        FormData
      >({
        query: (data) => {
          return {
            url: 'retailers/import',
            method: 'POST',
            data,
            headers: createTokenHeader(),
          };
        },
        invalidatesTags: ['list'],
      }),
      uploadFarmerConnectRetailerDistrictMapping:
        builder.mutation<
          TReferrerHistoryImportResponse,
          FormData
        >({
          query: (data) => {
            return {
              url: 'retailer-farmer-connects/retailers/import',
              method: 'POST',
              data,
              headers: createTokenHeader(),
            };
          },
        }),
      uploadFarmerConnectRetailerProducts: builder.mutation<
        TReferrerHistoryImportResponse,
        FormData
      >({
        query: (data) => {
          return {
            url: 'retailer-farmer-connects/retailers-products/import',
            method: 'POST',
            data,
            headers: createTokenHeader(),
          };
        },
        invalidatesTags: ['list'],
      }),
      retailersExport: builder.query<
        { url?: string },
        TExportRetailersRequestBody
      >({
        query: (data) => ({
          method: 'POST',
          url: 'retailers/export',
          data: data,
        }),
      }),
      getCreditPartnerList: builder.query<
        GeneralObject,
        void
      >({
        query: () => ({
          method: 'GET',
          url: `credit-partners`,
        }),
      }),
      getRetailerCreditDetails: builder.query<
        GeneralObject,
        {
          retailerId: string | null;
          creditType: string | null;
        }
      >({
        query: (params) => ({
          method: 'GET',
          url: `payment/v2/credit`,
          params, //retailerId, creditType
          //const creditRetailerDetailsUrl = this.baseURL + `payment/v2/credit?retailerId=${retailerId}&creditType=${creditType}`;
        }),
      }),
      disableCredits: builder.mutation<
        GeneralObject,
        TDisableCreditPeriodPayload
      >({
        query: (data) => {
          return {
            url: 'payment-mode',
            method: 'PUT',
            data,
          };
        },
      }),
      enableCredits: builder.mutation<
        GeneralObject,
        TEnableCreditPeriodPayload
      >({
        query: (data) => {
          return {
            url: 'payment-mode',
            method: 'POST',
            data,
          };
        },
      }),
      getAllDivisionList: builder.query<
        GeneralObject,
        string
      >({
        query: (companyId) => ({
          method: 'GET',
          url: `enterprise/${companyId}/divisions`,
        }),
      }),
      getCustomerBlockMessage: builder.query<
        GeneralObject,
        { retailerId: string; enterpriseId: string | null }
      >({
        query: ({ retailerId, enterpriseId }) => ({
          method: 'GET',
          url: `retailer/customerBlocks?retailerId=${retailerId}&enterpriseId=${enterpriseId}`,
        }),
      }),
      getCreditDetails: builder.query<
        GeneralObject,
        {
          retailerId: string;
          enterpriseId: string;
          divisionId: string;
        }
      >({
        query: ({
          retailerId,
          enterpriseId,
          divisionId,
        }) => ({
          method: 'GET',
          url: `retailer/credit?retailerId=${retailerId}&enterpriseId=${enterpriseId}&divisionCode=${divisionId}`,
        }),
      }),
      getRetailerKYCHistory: builder.query<
        KYCHistoryList,
        number
      >({
        query: (retailerId) => ({
          method: 'GET',
          url: `retailer-kyc-histories/list/${retailerId}`,
        }),
      }),
    }),
  });

export const {
  useGetRetailerInfoQuery,
  useGetAllVerifierNamesQuery,
  useGetAllRetailersQuery,
  useLazyDownloadLSMWFileQuery,
  useReferrerHistoryImportMutation,
  useRetailersImportMutation,
  useUploadFarmerConnectRetailerDistrictMappingMutation,
  useUploadFarmerConnectRetailerProductsMutation,
  useLazyRetailersExportQuery,
  useGetCreditPartnerListQuery,
  useGetRetailerCreditDetailsQuery,
  useLazyGetRetailerCreditDetailsQuery,
  useLazyGetCreditPartnerListQuery,
  useEnableCreditsMutation,
  useDisableCreditsMutation,
  useGetAllDivisionListQuery,
  useLazyGetAllDivisionListQuery,
  useGetCustomerBlockMessageQuery,
  useLazyGetCustomerBlockMessageQuery,
  useGetCreditDetailsQuery,
  useLazyGetCreditDetailsQuery,
  useGetRetailerKYCHistoryQuery,
} = retailerService;
